/**
 * Whether the API has told us this account is not on the pilot.
 *
 * The refusal arrives on an ordinary API response rather than at sign-in, so it
 * needs somewhere to live outside the request that saw it — and a module-level
 * flag rather than React state, because the API client has no component.
 */

import type { ErrorCode } from "@/lib/problem/error-words";

/** The code the API sends when this account is not admitted to the pilot. */
export const ACCESS_NOT_GRANTED: ErrorCode = "ACCESS_NOT_GRANTED";
/** The code the API sends when the address behind the sign-in is not verified yet. */
export const EMAIL_NOT_VERIFIED: ErrorCode = "EMAIL_NOT_VERIFIED";

/** Why the API refused this account: not admitted, or admitted only once the address is verified. */
export type AccessDenial = "not-granted" | "email-unverified";

// A Map rather than an object literal, so a code such as "toString" finds
// nothing rather than a property inherited from the prototype.
const DENIALS: ReadonlyMap<string, AccessDenial> = new Map([
  [ACCESS_NOT_GRANTED, "not-granted"],
  [EMAIL_NOT_VERIFIED, "email-unverified"],
]);

let denied: AccessDenial | null = null;
const listeners = new Set<() => void>();

function announce(): void {
  for (const listener of listeners) listener();
}

/** Record that the API refused this account, and why. Idempotent for the same reason. */
export function markAccessDenied(denial: AccessDenial = "not-granted"): void {
  if (denied === denial) return;
  denied = denial;
  announce();
}

/** Forget the refusal, so signing in as somebody else starts clean. */
export function clearAccessDenied(): void {
  if (denied === null) return;
  denied = null;
  announce();
}

export function isAccessDenied(): boolean {
  return denied !== null;
}

/** Why this account was refused, or null while it has not been. */
export function accessDenial(): AccessDenial | null {
  return denied;
}

export function subscribeAccessDenied(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Whether a failed response is this refusal. Keyed on the code: a 403 about one
 * particular thing must not blank the whole application, and the message is
 * prose somebody will reword.
 */
export function isAccessRefusal(status: number, body: unknown): boolean {
  return refusalIn(status, body) !== null;
}

/** Which refusal a failed response carries, or null if it carries neither. */
export function refusalIn(status: number, body: unknown): AccessDenial | null {
  if (status !== 403) return null;
  if (typeof body !== "object" || body === null) return null;
  const { error } = body as { error?: unknown };
  if (typeof error !== "object" || error === null) return null;
  const { code } = error as { code?: unknown };
  return typeof code === "string" ? (DENIALS.get(code) ?? null) : null;
}
